import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Trash2,
  RefreshCw,
  Loader2,
  Save,
  Plus,
  X as XIcon,
  RotateCcw,
  Users,
  CalendarDays,
  Star,
  Download,
  Clock,
  Sparkles,
  FileText,
  Instagram,
  Mail,
  Send,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "shx_admin_token";

const WORKSHOP_LABELS = {
  tecnica: "Tecnica Heels",
  laboratorio: "Laboratorio Coreografico",
  corso_completo: "Corso Completo",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const navigate = useNavigate();

  const login = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const t = res.data.token;
      localStorage.setItem(STORAGE_KEY, t);
      setToken(t);
      toast.success("Benvenuto Alessandro");
    } catch (e) {
      toast.error("Password non valida");
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setPassword("");
  };

  if (!token) {
    return (
      <div className="min-h-screen sun-bg text-[#2A1810] flex items-center justify-center p-6">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm p-10 shadow-[0_30px_60px_-20px_rgba(160,82,45,0.25)]"
        >
          <Lock size={28} className="text-[#A0522D] mb-5" />
          <p className="overline text-[#4F8FA8]">Area riservata</p>
          <h1 className="font-serif-display text-3xl mt-2 text-[#2A1810]">
            Dashboard Admin
          </h1>
          <p className="text-[#6E5B4A] text-sm mt-3">
            Inserisci la password per accedere alla gestione.
          </p>

          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-warm mt-8"
            placeholder="Password"
            autoFocus
          />

          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loginLoading}
            className="btn-primary w-full justify-center mt-5 disabled:opacity-40"
          >
            {loginLoading ? <Loader2 size={16} className="animate-spin" /> : "Accedi"}
          </button>

          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-[#6E5B4A] text-xs mt-5 hover:text-[#A0522D] transition block mx-auto"
          >
            ← Torna al sito
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7EEDB] text-[#2A1810]" data-testid="admin-dashboard">
      <header className="border-b border-[#D9C5A4] sticky top-0 z-40 bg-[#F7EEDB]/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="overline text-[#4F8FA8]">Admin</p>
            <h1 className="font-serif-display text-lg sm:text-2xl text-[#2A1810] truncate">
              Summer Heels Experience
            </h1>
          </div>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="btn-ghost !py-2 !px-3 sm:!px-4 flex-shrink-0"
          >
            <LogOut size={14} /> <span className="hidden sm:inline">Esci</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Tabs defaultValue="signups" className="w-full">
          <TabsList className="bg-[#FBF6EA] border border-[#D9C5A4] rounded-md p-1 mb-8">
            <TabsTrigger
              value="signups"
              data-testid="tab-signups"
              className="data-[state=active]:bg-[#A0522D] data-[state=active]:text-[#FBF6EA] text-[#6E5B4A] px-5"
            >
              <Users size={14} className="mr-2" />
              Iscrizioni
            </TabsTrigger>
            <TabsTrigger
              value="workshops"
              data-testid="tab-workshops"
              className="data-[state=active]:bg-[#A0522D] data-[state=active]:text-[#FBF6EA] text-[#6E5B4A] px-5"
            >
              <CalendarDays size={14} className="mr-2" />
              Workshop
            </TabsTrigger>
            <TabsTrigger
              value="experience"
              data-testid="tab-experience"
              className="data-[state=active]:bg-[#A0522D] data-[state=active]:text-[#FBF6EA] text-[#6E5B4A] px-5"
            >
              <Sparkles size={14} className="mr-2" />
              L&apos;Esperienza
            </TabsTrigger>
            <TabsTrigger
              value="pages"
              data-testid="tab-pages"
              className="data-[state=active]:bg-[#A0522D] data-[state=active]:text-[#FBF6EA] text-[#6E5B4A] px-5"
            >
              <FileText size={14} className="mr-2" />
              Pagine
            </TabsTrigger>
            <TabsTrigger
              value="instagram"
              data-testid="tab-instagram"
              className="data-[state=active]:bg-[#A0522D] data-[state=active]:text-[#FBF6EA] text-[#6E5B4A] px-5"
            >
              <Instagram size={14} className="mr-2" />
              Instagram
            </TabsTrigger>
            <TabsTrigger
              value="newsletter"
              data-testid="tab-newsletter"
              className="data-[state=active]:bg-[#A0522D] data-[state=active]:text-[#FBF6EA] text-[#6E5B4A] px-5"
            >
              <Mail size={14} className="mr-2" />
              Newsletter
            </TabsTrigger>
            <TabsTrigger
              value="reviews"
              data-testid="tab-reviews"
              className="data-[state=active]:bg-[#A0522D] data-[state=active]:text-[#FBF6EA] text-[#6E5B4A] px-5"
            >
              <Star size={14} className="mr-2" />
              Recensioni
            </TabsTrigger>
          </TabsList>

          <TabsContent value="signups">
            <SignupsTab token={token} onUnauthorized={logout} />
          </TabsContent>

          <TabsContent value="workshops">
            <WorkshopsTab token={token} onUnauthorized={logout} />
          </TabsContent>

          <TabsContent value="experience">
            <ExperienceTab token={token} onUnauthorized={logout} />
          </TabsContent>

          <TabsContent value="pages">
            <PagesTab token={token} onUnauthorized={logout} />
          </TabsContent>

          <TabsContent value="instagram">
            <InstagramTab token={token} onUnauthorized={logout} />
          </TabsContent>

          <TabsContent value="newsletter">
            <NewsletterTab token={token} onUnauthorized={logout} />
          </TabsContent>

          <TabsContent value="reviews">
            <ReviewsTab token={token} onUnauthorized={logout} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* ---------- SIGNUPS TAB ---------- */

const SignupsTab = ({ token, onUnauthorized }) => {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchSignups = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/signups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSignups(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Sessione scaduta");
        onUnauthorized();
      } else {
        toast.error("Errore nel caricamento");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onUnauthorized]);

  useEffect(() => {
    fetchSignups();
  }, [fetchSignups]);

  const deleteSignup = async (id) => {
    if (!window.confirm("Eliminare questa iscrizione?")) return;
    try {
      await axios.delete(`${API}/admin/signups/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSignups((s) => s.filter((x) => x.id !== id));
      toast.success("Iscrizione eliminata");
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  const exportCSV = () => {
    if (!signups.length) {
      toast.error("Nessuna iscrizione da esportare");
      return;
    }
    const headers = [
      "Data iscrizione",
      "Nome",
      "Cognome",
      "Email",
      "Telefono",
      "Città",
      "Età",
      "Codice Fiscale",
      "Workshop",
      "Livello",
      "Data workshop",
      "Prezzo",
      "Ha già danzato",
      "Tipo di danza",
      "Ha provato heels",
      "Aspettative",
    ];
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",;\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = signups.map((s) => [
      new Date(s.created_at).toLocaleString("it-IT"),
      s.first_name,
      s.last_name,
      s.email,
      s.phone,
      s.city,
      s.age,
      s.codice_fiscale || "",
      WORKSHOP_LABELS[s.workshop_type] || s.workshop_type,
      s.workshop_level,
      s.workshop_date,
      s.workshop_price || "",
      s.has_danced_before === "si" ? "Sì" : "No",
      s.dance_type || "",
      s.tried_heels === "si" ? "Sì" : "No",
      s.expectations || "",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map(escape).join(";"))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `iscrizioni_summer_heels_${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Esportate ${signups.length} iscrizioni`);
  };

  return (
    <>
      <div className="flex justify-end gap-2 mb-4">
        <button
          data-testid="admin-export-csv"
          onClick={exportCSV}
          className="btn-ghost !py-2 !px-4"
          disabled={loading || signups.length === 0}
          title="Esporta tutte le iscrizioni in CSV"
        >
          <Download size={14} />
          <span className="hidden sm:inline">Esporta CSV</span>
          <span className="sm:hidden">CSV</span>
        </button>
        <button
          data-testid="admin-refresh"
          onClick={fetchSignups}
          className="btn-ghost !py-2 !px-4"
          disabled={loading}
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          <span className="hidden sm:inline">Aggiorna</span>
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <Stat label="Iscrizioni totali" value={signups.length} accent="leather" />
        <Stat
          label="Ultimi 7 giorni"
          value={
            signups.filter((s) => {
              const d = new Date(s.created_at);
              return Date.now() - d.getTime() < 7 * 86400000;
            }).length
          }
          accent="azure"
        />
        <Stat
          label="Workshop più richiesto"
          value={mostPopular(signups)}
          small
          accent="leather"
        />
      </div>

      {loading && signups.length === 0 ? (
        <div className="text-center py-20 text-[#6E5B4A]">
          <Loader2 size={28} className="animate-spin mx-auto" />
          <p className="mt-4 overline">Caricamento…</p>
        </div>
      ) : signups.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#D9C5A4] rounded-sm bg-[#FBF6EA]">
          <p className="overline text-[#6E5B4A]">Nessuna iscrizione ancora</p>
          <p className="font-serif-display text-2xl mt-3 text-[#2A1810]/70">
            In attesa del prossimo workshop…
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {signups.map((s) => (
            <div
              key={s.id}
              data-testid={`signup-row-${s.id}`}
              className="bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm overflow-hidden shadow-[0_10px_24px_-18px_rgba(160,82,45,0.3)]"
            >
              <div
                role="button"
                tabIndex={0}
                onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setExpandedId(expandedId === s.id ? null : s.id);
                  }
                }}
                className="w-full grid grid-cols-12 gap-4 items-center p-5 text-left hover:bg-[#F1E4C9] transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#A0522D]/40"
              >
                <div className="col-span-12 sm:col-span-3">
                  <p className="overline text-[#6E5B4A]">Partecipante</p>
                  <p className="font-serif-display text-xl text-[#2A1810]">
                    {s.first_name} {s.last_name}
                  </p>
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <p className="overline text-[#6E5B4A]">Workshop</p>
                  <p className="text-sm text-[#2A1810]">
                    {WORKSHOP_LABELS[s.workshop_type] || s.workshop_type}
                  </p>
                  <p className="text-xs text-[#6E5B4A] capitalize">
                    {s.workshop_level} · {s.workshop_date}
                  </p>
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <p className="overline text-[#6E5B4A]">Contatto</p>
                  <p className="text-sm text-[#2A1810]">{s.email}</p>
                  <p className="text-xs text-[#6E5B4A]">{s.phone}</p>
                </div>
                <div className="col-span-12 sm:col-span-3 flex sm:justify-end items-center gap-3">
                  <span className="text-xs text-[#6E5B4A]">
                    {new Date(s.created_at).toLocaleDateString("it-IT")}
                  </span>
                  <button
                    data-testid={`delete-${s.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSignup(s.id);
                    }}
                    className="text-[#6E5B4A] hover:text-[#A0522D] transition p-2"
                    aria-label="elimina"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {expandedId === s.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="border-t border-[#D9C5A4] p-6 grid sm:grid-cols-2 gap-6 bg-[#F7EEDB]"
                >
                  <Detail label="Età" value={s.age} />
                  <Detail label="Codice Fiscale" value={s.codice_fiscale || "-"} />
                  <Detail label="Città" value={s.city} />
                  <Detail label="Prezzo" value={s.workshop_price || "-"} />
                  <Detail
                    label="Ha già fatto danza"
                    value={s.has_danced_before === "si" ? `Sì — ${s.dance_type || ""}` : "No"}
                  />
                  <Detail
                    label="Ha provato heels"
                    value={s.tried_heels === "si" ? "Sì" : "No"}
                  />
                  <Detail
                    label="Aspettative"
                    value={s.expectations}
                    className="sm:col-span-2"
                  />
                </motion.div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

/* ---------- WORKSHOPS TAB ---------- */

const WorkshopsTab = ({ token, onUnauthorized }) => {
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchWorkshops = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/workshops`);
      setWorkshops(res.data || []);
    } catch (e) {
      toast.error("Errore caricamento workshop");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkshops();
  }, [fetchWorkshops]);

  const handleReset = async () => {
    if (!window.confirm("Ripristinare i workshop ai valori di default? L'operazione non è reversibile."))
      return;
    try {
      await axios.post(
        `${API}/admin/workshops/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Workshop ripristinati");
      fetchWorkshops();
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized();
      else toast.error("Errore reset");
    }
  };

  if (loading && workshops.length === 0) {
    return (
      <div className="text-center py-20 text-[#6E5B4A]">
        <Loader2 size={28} className="animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <>
      <CountdownEditor token={token} onUnauthorized={onUnauthorized} />

      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="overline text-[#4F8FA8]">Gestione contenuti</p>
          <h2 className="font-serif-display text-2xl text-[#2A1810]">
            Modifica workshop, date e prezzi
          </h2>
        </div>
        <button
          onClick={handleReset}
          data-testid="reset-workshops-btn"
          className="btn-ghost !py-2 !px-4 !text-xs"
        >
          <RotateCcw size={14} /> Ripristina default
        </button>
      </div>

      <div className="space-y-6">
        {workshops.map((ws) => (
          <WorkshopEditor
            key={ws.id}
            workshop={ws}
            token={token}
            onUnauthorized={onUnauthorized}
            onSaved={fetchWorkshops}
          />
        ))}
      </div>
    </>
  );
};

const CountdownEditor = ({ token, onUnauthorized }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/countdown`)
      .then((res) => setData(res.data))
      .catch(() => toast.error("Errore caricamento countdown"));
  }, []);

  if (!data) return null;

  // Format for <input type="datetime-local">: YYYY-MM-DDTHH:mm in LOCAL time
  const toLocalInput = (iso) => {
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
        d.getDate()
      )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return "";
    }
  };

  const fromLocalInput = (local) => {
    if (!local) return data.target_iso;
    const d = new Date(local);
    return d.toISOString();
  };

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: data.enabled,
        target_iso: data.target_iso,
        label: data.label,
        expired_label: data.expired_label,
      };
      await axios.put(`${API}/admin/countdown`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Countdown aggiornato");
      // Bust banner dismiss so the user can see the new banner immediately
      try {
        sessionStorage.removeItem("shx_countdown_dismissed");
      } catch {
        /* noop */
      }
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized();
      else toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="countdown-editor"
      className="bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm overflow-hidden mb-6 shadow-[0_10px_24px_-18px_rgba(160,82,45,0.3)]"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F1E4C9] transition"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-sm bg-[#2A1810] text-[#F4C77B] flex items-center justify-center flex-shrink-0">
            <Clock size={20} />
          </div>
          <div>
            <p className="overline text-[#A0522D]">Banner urgenza</p>
            <h3 className="font-serif-display text-xl text-[#2A1810]">
              Countdown iscrizioni
            </h3>
            <p className="text-xs text-[#6E5B4A]">
              {data.enabled ? "Attivo" : "Disattivato"} ·{" "}
              {new Date(data.target_iso).toLocaleString("it-IT")}
            </p>
          </div>
        </div>
        <span className="text-[#A0522D] text-sm">
          {expanded ? "Chiudi" : "Modifica →"}
        </span>
      </button>

      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="border-t border-[#D9C5A4] p-6 space-y-5 bg-[#F7EEDB]"
        >
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                data-testid="countdown-enabled-toggle"
                type="checkbox"
                checked={data.enabled}
                onChange={(e) => update("enabled", e.target.checked)}
                className="w-4 h-4 accent-[#A0522D]"
              />
              <span className="text-sm text-[#2A1810]">
                Mostra il banner sul sito
              </span>
            </label>
          </div>

          <EditField label="Data e ora di chiusura iscrizioni">
            <input
              data-testid="countdown-target-input"
              type="datetime-local"
              value={toLocalInput(data.target_iso)}
              onChange={(e) =>
                update("target_iso", fromLocalInput(e.target.value))
              }
              className="input-warm"
            />
            <p className="text-xs text-[#6E5B4A] mt-2">
              Quando il timer arriva a zero, il banner mostrerà il testo
              «iscrizioni chiuse».
            </p>
          </EditField>

          <EditField label="Testo banner (attivo)">
            <input
              data-testid="countdown-label-input"
              type="text"
              value={data.label}
              onChange={(e) => update("label", e.target.value)}
              className="input-warm"
              maxLength={80}
              placeholder="Es. Iscrizioni aperte — restano"
            />
          </EditField>

          <EditField label="Testo banner (scaduto)">
            <input
              data-testid="countdown-expired-input"
              type="text"
              value={data.expired_label}
              onChange={(e) => update("expired_label", e.target.value)}
              className="input-warm"
              maxLength={80}
              placeholder="Es. Iscrizioni chiuse"
            />
          </EditField>

          <div className="flex justify-end pt-3 border-t border-[#D9C5A4]">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="save-countdown"
              className="btn-primary disabled:opacity-40"
            >
              {saving ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Save size={14} />
              )}
              Salva countdown
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

const WorkshopEditor = ({ workshop, token, onUnauthorized, onSaved }) => {
  const [draft, setDraft] = useState(() => structuredClone(workshop));
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setDraft(structuredClone(workshop));
  }, [workshop]);

  const updateField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const updateBullet = (i, v) =>
    setDraft((d) => ({ ...d, bullets: d.bullets.map((b, idx) => (idx === i ? v : b)) }));
  const addBullet = () =>
    setDraft((d) => ({ ...d, bullets: [...d.bullets, ""] }));
  const removeBullet = (i) =>
    setDraft((d) => ({ ...d, bullets: d.bullets.filter((_, idx) => idx !== i) }));

  const updateSession = (i, k, v) =>
    setDraft((d) => ({
      ...d,
      sessions: d.sessions.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)),
    }));
  const addSession = () =>
    setDraft((d) => ({
      ...d,
      sessions: [
        ...d.sessions,
        { level: "Base", date: "", time: "", price: "", capacity: 15 },
      ],
    }));
  const removeSession = (i) =>
    setDraft((d) => ({ ...d, sessions: d.sessions.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/workshops/${workshop.id}`,
        {
          title: draft.title,
          tagline: draft.tagline,
          description: draft.description,
          image: draft.image,
          bullets: draft.bullets,
          sessions: draft.sessions,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${draft.title} aggiornato`);
      onSaved();
    } catch (e) {
      if (e?.response?.status === 401) {
        onUnauthorized();
      } else {
        toast.error(e?.response?.data?.detail || "Errore salvataggio");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`workshop-editor-${workshop.id}`}
      className="bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm overflow-hidden shadow-[0_10px_24px_-18px_rgba(160,82,45,0.3)]"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F1E4C9] transition"
      >
        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 bg-cover bg-center rounded-sm flex-shrink-0"
            style={{ backgroundImage: `url(${workshop.image})` }}
          />
          <div>
            <p className="overline text-[#A0522D]">{workshop.id}</p>
            <h3 className="font-serif-display text-2xl text-[#2A1810]">
              {draft.title}
            </h3>
            <p className="text-xs text-[#6E5B4A]">
              {draft.sessions.length} {draft.sessions.length === 1 ? "sessione" : "sessioni"}
            </p>
          </div>
        </div>
        <span className="text-[#A0522D] text-sm">{expanded ? "Chiudi" : "Modifica →"}</span>
      </button>

      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="border-t border-[#D9C5A4] p-6 space-y-5 bg-[#F7EEDB]"
        >
          <EditField label="Titolo">
            <input
              data-testid={`edit-title-${workshop.id}`}
              type="text"
              value={draft.title}
              onChange={(e) => updateField("title", e.target.value)}
              className="input-warm"
            />
          </EditField>
          <EditField label="Sottotitolo">
            <input
              type="text"
              value={draft.tagline}
              onChange={(e) => updateField("tagline", e.target.value)}
              className="input-warm"
            />
          </EditField>
          <EditField label="Foto (URL immagine)">
            <div className="flex gap-3 items-start">
              <div
                className="w-24 h-24 bg-cover bg-center rounded-sm border border-[#D9C5A4] flex-shrink-0"
                style={{ backgroundImage: `url(${draft.image})` }}
              />
              <input
                data-testid={`edit-image-${workshop.id}`}
                type="url"
                value={draft.image}
                onChange={(e) => updateField("image", e.target.value)}
                className="input-warm flex-1"
                placeholder="https://..."
              />
            </div>
            <p className="text-xs text-[#6E5B4A] mt-2">
              Incolla qui l&apos;URL dell&apos;immagine. Vedi sotto come ottenerlo.
            </p>
          </EditField>
          <EditField label="Descrizione">
            <textarea
              rows={5}
              value={draft.description}
              onChange={(e) => updateField("description", e.target.value)}
              className="input-warm resize-none"
            />
          </EditField>

          <EditField label="Punti chiave">
            <div className="space-y-2">
              {draft.bullets.map((b, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="text"
                    value={b}
                    onChange={(e) => updateBullet(i, e.target.value)}
                    className="input-warm flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeBullet(i)}
                    className="p-2 text-[#6E5B4A] hover:text-[#A0522D] transition"
                    aria-label="rimuovi"
                  >
                    <XIcon size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addBullet}
                className="text-xs text-[#A0522D] hover:underline flex items-center gap-1"
              >
                <Plus size={14} /> Aggiungi punto
              </button>
            </div>
          </EditField>

          <EditField label="Sessioni (livello, data, orario, prezzo, posti)">
            <div className="space-y-2">
              {draft.sessions.map((s, i) => (
                <div
                  key={i}
                  className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-center bg-[#FBF6EA] p-3 rounded-sm border border-[#D9C5A4]"
                >
                  <Select
                    value={s.level}
                    onValueChange={(v) => updateSession(i, "level", v)}
                  >
                    <SelectTrigger
                      data-testid={`session-level-${workshop.id}-${i}`}
                      className="col-span-2 sm:col-span-3 bg-[#FBF6EA] border-[#D9C5A4] hover:border-[#A0522D]/60 focus:border-[#A0522D] focus:ring-0 text-[#2A1810] text-sm rounded-md transition data-[placeholder]:text-[#A8987F] !h-auto !py-2"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FBF6EA] border-[#D9C5A4] text-[#2A1810]">
                      <SelectItem
                        value="Base"
                        className="text-[#2A1810] focus:bg-[#A0522D]/15 focus:text-[#2A1810] data-[state=checked]:text-[#A0522D] cursor-pointer"
                      >
                        Base
                      </SelectItem>
                      <SelectItem
                        value="Intermedio"
                        className="text-[#2A1810] focus:bg-[#A0522D]/15 focus:text-[#2A1810] data-[state=checked]:text-[#A0522D] cursor-pointer"
                      >
                        Intermedio
                      </SelectItem>
                      <SelectItem
                        value="Avanzato"
                        className="text-[#2A1810] focus:bg-[#A0522D]/15 focus:text-[#2A1810] data-[state=checked]:text-[#A0522D] cursor-pointer"
                      >
                        Avanzato
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <input
                    type="text"
                    value={s.date}
                    onChange={(e) => updateSession(i, "date", e.target.value)}
                    placeholder="Sab 14 Giu 2026"
                    className="input-warm col-span-2 sm:col-span-3 !py-2"
                  />
                  <input
                    type="text"
                    value={s.time}
                    onChange={(e) => updateSession(i, "time", e.target.value)}
                    placeholder="10:00 – 12:00"
                    className="input-warm col-span-1 sm:col-span-2 !py-2"
                  />
                  <input
                    type="text"
                    value={s.price}
                    onChange={(e) => updateSession(i, "price", e.target.value)}
                    placeholder="€35"
                    className="input-warm col-span-1 sm:col-span-1 !py-2"
                  />
                  <input
                    type="number"
                    min="1"
                    max="999"
                    value={s.capacity ?? 15}
                    onChange={(e) =>
                      updateSession(i, "capacity", parseInt(e.target.value, 10) || 15)
                    }
                    data-testid={`session-capacity-${workshop.id}-${i}`}
                    placeholder="15"
                    title="Posti massimi"
                    className="input-warm col-span-1 sm:col-span-2 !py-2"
                  />
                  <button
                    type="button"
                    onClick={() => removeSession(i)}
                    className="col-span-2 sm:col-span-1 p-2 text-[#6E5B4A] hover:text-[#A0522D] transition justify-self-end"
                    aria-label="rimuovi sessione"
                  >
                    <XIcon size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addSession}
                className="text-xs text-[#A0522D] hover:underline flex items-center gap-1"
              >
                <Plus size={14} /> Aggiungi sessione
              </button>
            </div>
          </EditField>

          <div className="flex justify-end pt-3 border-t border-[#D9C5A4]">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid={`save-${workshop.id}`}
              className="btn-primary disabled:opacity-40"
            >
              {saving ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Save size={14} />
              )}
              Salva modifiche
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

/* ---------- Shared ---------- */

const EditField = ({ label, children }) => (
  <div>
    <label className="overline text-[#6E5B4A] block mb-2">{label}</label>
    {children}
  </div>
);

const Stat = ({ label, value, small, accent = "leather" }) => {
  const accentColor = accent === "azure" ? "#4F8FA8" : "#A0522D";
  return (
    <div className="bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm p-6 shadow-[0_10px_24px_-18px_rgba(160,82,45,0.3)]">
      <p className="overline" style={{ color: accentColor }}>
        {label}
      </p>
      <p
        className={`font-serif-display mt-2 text-[#2A1810] ${
          small ? "text-2xl" : "text-4xl"
        }`}
      >
        {value}
      </p>
    </div>
  );
};

const Detail = ({ label, value, className = "" }) => (
  <div className={className}>
    <p className="overline text-[#6E5B4A] mb-1">{label}</p>
    <p className="text-[#2A1810] text-sm leading-relaxed">{value}</p>
  </div>
);

function mostPopular(signups) {
  if (!signups.length) return "—";
  const counts = signups.reduce((acc, s) => {
    acc[s.workshop_type] = (acc[s.workshop_type] || 0) + 1;
    return acc;
  }, {});
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return WORKSHOP_LABELS[top[0]] || top[0];
}

/* ---------- REVIEWS TAB ---------- */

const ReviewsTab = ({ token, onUnauthorized }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reviews`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReviews(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized();
      else toast.error("Errore caricamento recensioni");
    } finally {
      setLoading(false);
    }
  }, [token, onUnauthorized]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const deleteReview = async (id) => {
    if (!window.confirm("Eliminare questa recensione?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReviews((r) => r.filter((x) => x.id !== id));
      toast.success("Recensione eliminata");
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="overline text-[#4F8FA8]">Gestione recensioni</p>
          <h2 className="font-serif-display text-2xl text-[#2A1810]">
            {reviews.length} {reviews.length === 1 ? "recensione" : "recensioni"} pubblicate
          </h2>
        </div>
        <button
          onClick={fetchReviews}
          className="btn-ghost !py-2 !px-4"
          disabled={loading}
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Aggiorna
        </button>
      </div>

      {loading && reviews.length === 0 ? (
        <div className="text-center py-16 text-[#6E5B4A]">
          <Loader2 size={28} className="animate-spin mx-auto" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#D9C5A4] rounded-sm bg-[#FBF6EA]">
          <p className="overline text-[#6E5B4A]">Nessuna recensione</p>
          <p className="font-serif-display text-2xl mt-3 text-[#2A1810]/70">
            Le recensioni delle allieve appariranno qui
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div
              key={r.id}
              data-testid={`review-row-${r.id}`}
              className="bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm p-5 shadow-[0_10px_24px_-18px_rgba(160,82,45,0.3)]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <p className="font-serif-display text-xl text-[#2A1810]">
                      {r.name}
                    </p>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          size={13}
                          className={
                            i <= r.rating
                              ? "fill-[#A0522D] text-[#A0522D]"
                              : "text-[#D9C5A4]"
                          }
                        />
                      ))}
                    </div>
                    <span className="text-xs text-[#6E5B4A]">
                      {new Date(r.created_at).toLocaleDateString("it-IT")}
                    </span>
                  </div>
                  <p className="text-[#4A3B30] text-sm leading-relaxed italic">
                    &ldquo;{r.text}&rdquo;
                  </p>
                </div>
                <button
                  onClick={() => deleteReview(r.id)}
                  className="text-[#6E5B4A] hover:text-[#A0522D] transition p-2 flex-shrink-0"
                  aria-label="elimina"
                  data-testid={`delete-review-${r.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

/* ---------- EXPERIENCE TAB ---------- */

const ExperienceTab = ({ token, onUnauthorized }) => {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchExp = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/experience`);
      setDraft(res.data);
    } catch (e) {
      toast.error("Errore caricamento contenuti");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExp();
  }, [fetchExp]);

  if (loading || !draft) {
    return (
      <div className="text-center py-20 text-[#6E5B4A]">
        <Loader2 size={28} className="animate-spin mx-auto" />
      </div>
    );
  }

  const update = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const updateParagraph = (i, v) =>
    setDraft((d) => ({
      ...d,
      paragraphs: d.paragraphs.map((p, idx) => (idx === i ? v : p)),
    }));
  const addParagraph = () =>
    setDraft((d) => ({ ...d, paragraphs: [...d.paragraphs, ""] }));
  const removeParagraph = (i) =>
    setDraft((d) => ({
      ...d,
      paragraphs: d.paragraphs.filter((_, idx) => idx !== i),
    }));
  const updateStat = (i, k, v) =>
    setDraft((d) => ({
      ...d,
      stats: d.stats.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)),
    }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/experience`, draft, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Contenuti aggiornati");
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized();
      else toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Ripristinare i testi originali? Le modifiche andranno perse."))
      return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/experience/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDraft(res.data);
      toast.success("Testi ripristinati");
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized();
      else toast.error("Errore ripristino");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="overline text-[#4F8FA8]">Sezione &quot;L&apos;Esperienza&quot;</p>
          <h2 className="font-serif-display text-2xl text-[#2A1810]">
            Modifica testi, foto e contatori
          </h2>
        </div>
        <button
          onClick={reset}
          data-testid="reset-experience-btn"
          className="btn-ghost !py-2 !px-4 !text-xs"
        >
          <RotateCcw size={14} /> Ripristina default
        </button>
      </div>

      <div className="bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm p-5 sm:p-7 space-y-6 shadow-[0_10px_24px_-18px_rgba(160,82,45,0.3)]">
        {/* IMAGE */}
        <EditField label="URL foto Alessandro">
          <input
            data-testid="exp-image-url"
            type="text"
            value={draft.image_url}
            onChange={(e) => update("image_url", e.target.value)}
            className="input-warm"
            placeholder="https://..."
          />
          {draft.image_url && (
            <div className="mt-3 relative w-32 aspect-[3/4] overflow-hidden rounded-sm border border-[#D9C5A4]">
              <img
                src={draft.image_url}
                alt="anteprima"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          )}
        </EditField>

        <div className="grid sm:grid-cols-2 gap-5">
          <EditField label="Etichetta foto (sopra al nome)">
            <input
              data-testid="exp-badge-overline"
              type="text"
              value={draft.badge_overline}
              onChange={(e) => update("badge_overline", e.target.value)}
              className="input-warm"
              maxLength={30}
            />
          </EditField>
          <EditField label="Nome sulla foto">
            <input
              data-testid="exp-badge-name"
              type="text"
              value={draft.badge_name}
              onChange={(e) => update("badge_name", e.target.value)}
              className="input-warm"
              maxLength={40}
            />
          </EditField>
        </div>

        {/* HEADINGS */}
        <div className="pt-4 border-t border-[#D9C5A4]">
          <p className="overline text-[#A0522D] mb-3">Titolo principale (3 righe)</p>
          <div className="space-y-3">
            <EditField label="Riga 1 (normale)">
              <input
                data-testid="exp-heading-line1"
                type="text"
                value={draft.heading_line1}
                onChange={(e) => update("heading_line1", e.target.value)}
                className="input-warm"
                maxLength={50}
              />
            </EditField>
            <EditField label="Riga 2 (corsivo terracotta)">
              <input
                data-testid="exp-heading-italic"
                type="text"
                value={draft.heading_italic}
                onChange={(e) => update("heading_italic", e.target.value)}
                className="input-warm"
                maxLength={50}
              />
            </EditField>
            <EditField label="Riga 3 (normale)">
              <input
                data-testid="exp-heading-line3"
                type="text"
                value={draft.heading_line3}
                onChange={(e) => update("heading_line3", e.target.value)}
                className="input-warm"
                maxLength={50}
              />
            </EditField>
          </div>
        </div>

        <EditField label="Etichetta sopra al titolo">
          <input
            data-testid="exp-eyebrow"
            type="text"
            value={draft.eyebrow}
            onChange={(e) => update("eyebrow", e.target.value)}
            className="input-warm"
            maxLength={40}
          />
        </EditField>

        {/* PARAGRAPHS */}
        <div className="pt-4 border-t border-[#D9C5A4]">
          <div className="flex justify-between items-center mb-3">
            <p className="overline text-[#A0522D]">
              Paragrafi descrizione ({draft.paragraphs.length})
            </p>
            <button
              type="button"
              onClick={addParagraph}
              data-testid="exp-add-paragraph"
              className="btn-ghost !py-1.5 !px-3 !text-xs"
            >
              <Plus size={12} /> Aggiungi paragrafo
            </button>
          </div>
          <div className="space-y-3">
            {draft.paragraphs.map((p, i) => (
              <div key={i} className="flex gap-2">
                <textarea
                  data-testid={`exp-paragraph-${i}`}
                  value={p}
                  onChange={(e) => updateParagraph(i, e.target.value)}
                  rows={3}
                  className="input-warm flex-1 !leading-relaxed"
                  placeholder={`Paragrafo ${i + 1}…`}
                />
                <button
                  type="button"
                  onClick={() => removeParagraph(i)}
                  data-testid={`exp-remove-paragraph-${i}`}
                  className="text-[#A0522D] hover:bg-[#A0522D]/10 p-2 rounded-sm self-start"
                  aria-label={`elimina paragrafo ${i + 1}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* STATS */}
        <div className="pt-4 border-t border-[#D9C5A4]">
          <p className="overline text-[#A0522D] mb-3">
            Contatori statistiche (3 in totale)
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            {draft.stats.slice(0, 3).map((s, i) => (
              <div
                key={i}
                className="bg-[#F7EEDB] border border-[#D9C5A4] rounded-sm p-4 space-y-3"
              >
                <p className="text-[10px] tracking-widest uppercase text-[#A0522D]">
                  Stat #{i + 1}
                </p>
                <EditField label="Numero">
                  <input
                    data-testid={`exp-stat-num-${i}`}
                    type="text"
                    value={s.num}
                    onChange={(e) => updateStat(i, "num", e.target.value)}
                    className="input-warm font-serif-display !text-2xl"
                    maxLength={10}
                  />
                </EditField>
                <EditField label="Etichetta">
                  <input
                    data-testid={`exp-stat-label-${i}`}
                    type="text"
                    value={s.label}
                    onChange={(e) => updateStat(i, "label", e.target.value)}
                    className="input-warm"
                    maxLength={40}
                  />
                </EditField>
              </div>
            ))}
          </div>
        </div>

        {/* SAVE */}
        <div className="flex justify-end pt-4 border-t border-[#D9C5A4]">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="save-experience"
            className="btn-primary disabled:opacity-40"
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Save size={14} />
            )}
            Salva modifiche
          </button>
        </div>
      </div>
    </>
  );
};

/* ---------- PAGES TAB ---------- */

const PAGES_DEFAULTS = {
  hero: {
    eyebrow: "Estate 2026 — con Alessandro",
    title_line1: "Summer Heels",
    title_italic: "Experience",
    description: "",
    cta_primary: "Scopri i Workshop",
    cta_secondary: "Iscriviti ora",
  },
  workshops: { eyebrow: "", heading_line1: "", heading_italic: "", description: "" },
  reviews: { eyebrow: "", heading_line1: "", heading_italic: "", description: "" },
  footer: {
    brand_part1: "Summer",
    brand_part2: "Heels",
    brand_part3: "Experience",
    copyright: "© 2026 · Con Alessandro",
    email: "",
    instagram_url: "",
  },
};

const PagesTab = ({ token, onUnauthorized }) => {
  const [pages, setPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState("hero");

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pages`);
      // Merge with defaults so missing fields don't crash
      setPages({
        hero: { ...PAGES_DEFAULTS.hero, ...(res.data?.hero || {}) },
        workshops: { ...PAGES_DEFAULTS.workshops, ...(res.data?.workshops || {}) },
        reviews: { ...PAGES_DEFAULTS.reviews, ...(res.data?.reviews || {}) },
        footer: { ...PAGES_DEFAULTS.footer, ...(res.data?.footer || {}) },
      });
    } catch (e) {
      toast.error("Errore caricamento contenuti");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  if (loading || !pages) {
    return (
      <div className="text-center py-20 text-[#6E5B4A]">
        <Loader2 size={28} className="animate-spin mx-auto" />
      </div>
    );
  }

  const updateSection = (section, key, value) =>
    setPages((p) => ({ ...p, [section]: { ...p[section], [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages`, pages, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Pagine aggiornate");
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized();
      else toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Ripristinare i testi originali di TUTTE le pagine?"))
      return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/pages/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPages(res.data);
      toast.success("Testi pagine ripristinati");
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized();
      else toast.error("Errore ripristino");
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    {
      key: "hero",
      label: "Prima schermata (Hero)",
      desc: "Eyebrow, titolo grande, descrizione e pulsanti",
    },
    {
      key: "workshops",
      label: "Titolo sezione Workshop",
      desc: "Intestazione sopra alle 3 card workshop",
    },
    {
      key: "reviews",
      label: "Titolo sezione Recensioni",
      desc: "Intestazione sopra alle recensioni delle allieve",
    },
    {
      key: "footer",
      label: "Footer",
      desc: "Logo testuale, copyright, email, link Instagram",
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="overline text-[#4F8FA8]">Testi del sito pubblico</p>
          <h2 className="font-serif-display text-2xl text-[#2A1810]">
            Modifica ogni sezione della home
          </h2>
        </div>
        <button
          onClick={reset}
          data-testid="reset-pages-btn"
          className="btn-ghost !py-2 !px-4 !text-xs"
        >
          <RotateCcw size={14} /> Ripristina default
        </button>
      </div>

      <div className="space-y-4">
        {sections.map((s) => (
          <PageSectionCard
            key={s.key}
            section={s}
            data={pages[s.key]}
            isOpen={expanded === s.key}
            onToggle={() => setExpanded(expanded === s.key ? null : s.key)}
            onChange={(k, v) => updateSection(s.key, k, v)}
          />
        ))}
      </div>

      <div className="sticky bottom-0 z-10 mt-8 bg-[#F7EEDB]/95 backdrop-blur border-t border-[#D9C5A4] py-4 -mx-6 px-6 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="save-pages"
          className="btn-primary disabled:opacity-40"
        >
          {saving ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Save size={14} />
          )}
          Salva tutte le pagine
        </button>
      </div>
    </>
  );
};

const PageSectionCard = ({ section, data, isOpen, onToggle, onChange }) => {
  const fieldsConfig = {
    hero: [
      { k: "eyebrow", label: "Etichetta sopra al titolo", type: "text", max: 80 },
      { k: "title_line1", label: "Titolo riga 1 (normale)", type: "text", max: 50 },
      { k: "title_italic", label: "Titolo riga 2 (corsivo terracotta)", type: "text", max: 50 },
      { k: "description", label: "Descrizione (paragrafo sotto al titolo)", type: "textarea", max: 400 },
      { k: "cta_primary", label: "Pulsante primario (scuro)", type: "text", max: 40 },
      { k: "cta_secondary", label: "Pulsante secondario (azzurro)", type: "text", max: 40 },
      { k: "logo_url", label: "URL logo nel cerchio bianco", type: "image", max: 500 },
    ],
    workshops: [
      { k: "eyebrow", label: "Etichetta sopra al titolo", type: "text", max: 60 },
      { k: "heading_line1", label: "Titolo riga 1 (normale)", type: "text", max: 60 },
      { k: "heading_italic", label: "Titolo riga 2 (corsivo terracotta)", type: "text", max: 60 },
      { k: "description", label: "Descrizione sotto al titolo (opzionale)", type: "textarea", max: 300 },
    ],
    reviews: [
      { k: "eyebrow", label: "Etichetta sopra al titolo", type: "text", max: 60 },
      { k: "heading_line1", label: "Titolo riga 1 (normale)", type: "text", max: 60 },
      { k: "heading_italic", label: "Titolo riga 2 (corsivo terracotta)", type: "text", max: 60 },
      { k: "description", label: "Descrizione sotto al titolo (opzionale)", type: "textarea", max: 300 },
    ],
    footer: [
      { k: "brand_part1", label: "Logo parola 1 (color terracotta)", type: "text", max: 30 },
      { k: "brand_part2", label: "Logo parola 2 (color crema)", type: "text", max: 30 },
      { k: "brand_part3", label: "Logo parola 3 (corsivo azzurro)", type: "text", max: 30 },
      { k: "copyright", label: "Testo copyright in basso", type: "text", max: 100 },
      { k: "email", label: "Email contatto (vuoto = nascondi)", type: "text", max: 100 },
      { k: "instagram_url", label: "URL Instagram (vuoto = nascondi)", type: "text", max: 200 },
    ],
  };
  const fields = fieldsConfig[section.key] || [];

  return (
    <div className="bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm overflow-hidden shadow-[0_10px_24px_-18px_rgba(160,82,45,0.3)]">
      <button
        type="button"
        onClick={onToggle}
        data-testid={`pages-toggle-${section.key}`}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F1E4C9] transition"
      >
        <div>
          <p className="overline text-[#A0522D]">{section.label}</p>
          <p className="text-xs text-[#6E5B4A] mt-1">{section.desc}</p>
        </div>
        <span className="text-[#A0522D] text-sm">
          {isOpen ? "Chiudi" : "Modifica →"}
        </span>
      </button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="border-t border-[#D9C5A4] p-6 space-y-5 bg-[#F7EEDB]"
        >
          {fields.map((f) => (
            <EditField key={f.k} label={f.label}>
              {f.type === "textarea" ? (
                <textarea
                  data-testid={`pages-${section.key}-${f.k}`}
                  value={data[f.k] || ""}
                  onChange={(e) => onChange(f.k, e.target.value)}
                  rows={3}
                  maxLength={f.max}
                  className="input-warm !leading-relaxed"
                />
              ) : f.type === "image" ? (
                <>
                  <input
                    data-testid={`pages-${section.key}-${f.k}`}
                    type="url"
                    value={data[f.k] || ""}
                    onChange={(e) => onChange(f.k, e.target.value)}
                    maxLength={f.max}
                    placeholder="https://..."
                    className="input-warm"
                  />
                  {data[f.k] && (
                    <div className="mt-3 inline-block bg-white border border-[#D9C5A4] rounded-sm p-2">
                      <img
                        src={data[f.k]}
                        alt="anteprima"
                        className="max-h-24 w-auto object-contain"
                      />
                    </div>
                  )}
                </>
              ) : (
                <input
                  data-testid={`pages-${section.key}-${f.k}`}
                  type="text"
                  value={data[f.k] || ""}
                  onChange={(e) => onChange(f.k, e.target.value)}
                  maxLength={f.max}
                  className="input-warm"
                />
              )}
            </EditField>
          ))}
        </motion.div>
      )}
    </div>
  );
};


/* ---------- INSTAGRAM TAB ---------- */

const INSTAGRAM_DEFAULTS = {
  enabled: true,
  eyebrow: "Dal mio Instagram",
  heading_line1: "Seguimi",
  heading_italic: "@mantelheelsexperience",
  description: "Backstage, performance e momenti dal vivo dei workshop.",
  posts: [],
};

const isValidInstagramUrl = (url) =>
  /instagram\.com\/(?:p|reel|tv)\/[a-zA-Z0-9_-]+/.test(url || "");

const InstagramTab = ({ token, onUnauthorized }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/instagram`);
      setData({ ...INSTAGRAM_DEFAULTS, ...(res.data || {}) });
    } catch (e) {
      toast.error("Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading || !data) {
    return (
      <div className="text-center py-20 text-[#6E5B4A]">
        <Loader2 size={28} className="animate-spin mx-auto" />
      </div>
    );
  }

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const updatePost = (i, k, v) =>
    setData((d) => ({
      ...d,
      posts: d.posts.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)),
    }));
  const addPost = () =>
    setData((d) => ({
      ...d,
      posts: [...d.posts, { url: "", caption: "" }],
    }));
  const removePost = (i) =>
    setData((d) => ({ ...d, posts: d.posts.filter((_, idx) => idx !== i) }));

  const save = async () => {
    // Filter out posts with invalid/empty URLs
    const validPosts = data.posts.filter((p) => isValidInstagramUrl(p.url));
    const invalidCount = data.posts.length - validPosts.length;
    if (invalidCount > 0) {
      toast.error(
        `${invalidCount} URL non validi verranno scartati. Usa link tipo https://www.instagram.com/p/...`
      );
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/instagram`,
        { ...data, posts: validPosts },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData((d) => ({ ...d, posts: validPosts }));
      toast.success("Feed Instagram aggiornato");
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized();
      else toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <p className="overline text-[#4F8FA8]">Feed Instagram</p>
          <h2 className="font-serif-display text-2xl text-[#2A1810]">
            Post Instagram in evidenza
          </h2>
          <p className="text-xs text-[#6E5B4A] mt-1 max-w-2xl">
            Incolla URL di post pubblici di Instagram (es. https://www.instagram.com/p/XXXXX/).
            Verranno mostrati come embed sulla home, sotto le recensioni. Lascia vuoto per nascondere la sezione.
          </p>
        </div>
      </div>

      <div className="bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm p-5 sm:p-6 space-y-5 mb-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            data-testid="instagram-enabled"
            type="checkbox"
            checked={data.enabled}
            onChange={(e) => update("enabled", e.target.checked)}
            className="w-4 h-4 accent-[#A0522D]"
          />
          <span className="text-sm text-[#2A1810]">
            Mostra la sezione Instagram sulla home
          </span>
        </label>

        <div className="grid sm:grid-cols-2 gap-4">
          <EditField label="Etichetta sopra al titolo">
            <input
              data-testid="instagram-eyebrow"
              type="text"
              value={data.eyebrow}
              onChange={(e) => update("eyebrow", e.target.value)}
              className="input-warm"
              maxLength={60}
            />
          </EditField>
          <EditField label="Titolo riga 1 (normale)">
            <input
              data-testid="instagram-heading-line1"
              type="text"
              value={data.heading_line1}
              onChange={(e) => update("heading_line1", e.target.value)}
              className="input-warm"
              maxLength={60}
            />
          </EditField>
          <EditField label="Titolo riga 2 (corsivo terracotta)">
            <input
              data-testid="instagram-heading-italic"
              type="text"
              value={data.heading_italic}
              onChange={(e) => update("heading_italic", e.target.value)}
              className="input-warm"
              maxLength={60}
            />
          </EditField>
          <EditField label="Descrizione sotto al titolo">
            <input
              data-testid="instagram-description"
              type="text"
              value={data.description}
              onChange={(e) => update("description", e.target.value)}
              className="input-warm"
              maxLength={200}
            />
          </EditField>
        </div>
      </div>

      {/* POSTS LIST */}
      <div className="flex justify-between items-center mb-3">
        <p className="overline text-[#A0522D]">
          Post da mostrare ({data.posts.length})
        </p>
        <button
          type="button"
          onClick={addPost}
          data-testid="instagram-add-post"
          className="btn-ghost !py-2 !px-4 !text-xs"
        >
          <Plus size={14} /> Aggiungi post
        </button>
      </div>

      <div className="space-y-3 mb-6">
        {data.posts.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-[#D9C5A4] rounded-sm bg-[#FBF6EA]">
            <p className="text-sm text-[#6E5B4A]">
              Nessun post. Aggiungine uno per iniziare.
            </p>
            <p className="text-xs text-[#A8987F] mt-2">
              Su Instagram → apri il post → menu (···) → &laquo;Copia link&raquo;
            </p>
          </div>
        ) : (
          data.posts.map((p, i) => {
            const isValid = isValidInstagramUrl(p.url);
            return (
              <div
                key={i}
                data-testid={`instagram-post-row-${i}`}
                className="bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm p-4 flex flex-col sm:flex-row gap-3"
              >
                <div className="flex-1 space-y-2">
                  <input
                    data-testid={`instagram-post-url-${i}`}
                    type="url"
                    value={p.url}
                    onChange={(e) => updatePost(i, "url", e.target.value)}
                    placeholder="https://www.instagram.com/p/XXXXX/"
                    className={`input-warm ${
                      p.url && !isValid ? "!border-[#A02525]" : ""
                    }`}
                  />
                  {p.url && !isValid && (
                    <p className="text-xs text-[#A02525]">
                      URL non valido. Deve essere tipo https://www.instagram.com/p/...
                    </p>
                  )}
                  <input
                    data-testid={`instagram-post-caption-${i}`}
                    type="text"
                    value={p.caption || ""}
                    onChange={(e) => updatePost(i, "caption", e.target.value)}
                    placeholder="Didascalia interna (opzionale, non visibile)"
                    maxLength={200}
                    className="input-warm !py-2 !text-xs"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removePost(i)}
                  data-testid={`instagram-remove-post-${i}`}
                  className="text-[#A0522D] hover:bg-[#A0522D]/10 p-2 rounded-sm self-start"
                  aria-label="rimuovi"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })
        )}
      </div>

      <div className="sticky bottom-0 z-10 bg-[#F7EEDB]/95 backdrop-blur border-t border-[#D9C5A4] py-4 -mx-6 px-6 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="save-instagram"
          className="btn-primary disabled:opacity-40"
        >
          {saving ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Save size={14} />
          )}
          Salva feed Instagram
        </button>
      </div>
    </>
  );
};


/* ---------- NEWSLETTER TAB ---------- */

const buildNewWorkshopTemplate = () => ({
  subject: "Nuovo workshop in arrivo!",
  body: `Ciao!

Ho il piacere di annunciarti un nuovo workshop in arrivo. Tutti i dettagli (date, livelli e prezzi) li trovi sul sito.

Le iscrizioni sono già aperte — i posti sono limitati, ti consiglio di scegliere il tuo prima possibile.

A presto in pista,
Alessandro`,
});

const buildNewDateTemplate = () => ({
  subject: "Nuova data disponibile",
  body: `Ciao!

È stata aperta una nuova data per uno dei nostri workshop. Vai sul sito per scoprire livello, orari e prezzi.

I posti sono limitati — assicurati il tuo prima che si esauriscano.

A presto in pista,
Alessandro`,
});

const buildNewLessonTemplate = () => ({
  subject: "Nuova lezione disponibile",
  body: `Ciao!

Ho appena aggiunto una nuova lezione al programma estivo. Trovi tutti i dettagli su mantelheelsexperience.com.

Ti aspetto in sala!
Alessandro`,
});

const NewsletterTab = ({ token, onUnauthorized }) => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/newsletter`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscribers(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized();
      else toast.error("Errore caricamento iscritti");
    } finally {
      setLoading(false);
    }
  }, [token, onUnauthorized]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const removeSubscriber = async (id) => {
    if (!window.confirm("Rimuovere questo iscritto dalla newsletter?")) return;
    try {
      await axios.delete(`${API}/admin/newsletter/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscribers((s) => s.filter((x) => x.id !== id));
      toast.success("Iscritto rimosso");
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized();
      else toast.error("Errore rimozione");
    }
  };

  const exportCSV = () => {
    if (!subscribers.length) {
      toast.error("Nessun iscritto da esportare");
      return;
    }
    const headers = ["Data iscrizione", "Nome", "Email"];
    const escape = (v) => {
      const s = String(v ?? "").replace(/"/g, '""');
      return /[",;\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = subscribers.map((s) => [
      new Date(s.created_at).toLocaleString("it-IT"),
      s.name || "",
      s.email,
    ]);
    const csv = [headers, ...rows].map((r) => r.map(escape).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `newsletter_iscritti_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${subscribers.length} iscritti esportati`);
  };

  const applyTemplate = (builder) => {
    const t = builder();
    setSubject(t.subject);
    setBody(t.body);
  };

  const sendCampaign = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Compila oggetto e testo prima di inviare");
      return;
    }
    if (subscribers.length === 0) {
      toast.error("Non ci sono iscritti a cui inviare");
      return;
    }
    if (
      !window.confirm(
        `Sicuro di inviare a tutti i ${subscribers.length} iscritti? Questa azione non può essere annullata.`
      )
    )
      return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/newsletter/send`,
        { subject: subject.trim(), body_text: body.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `Inviato a ${res.data.sent} iscritti${
          res.data.failed > 0 ? ` (${res.data.failed} falliti)` : ""
        }`
      );
      setSubject("");
      setBody("");
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized();
      else toast.error(e?.response?.data?.detail || "Errore invio campagna");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="mb-6">
        <p className="overline text-[#4F8FA8]">Newsletter</p>
        <h2 className="font-serif-display text-2xl text-[#2A1810]">
          Gestione iscritti e invio comunicazioni
        </h2>
      </div>

      {/* COMPOSE BOX */}
      <div className="bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm p-5 sm:p-7 mb-6 shadow-[0_10px_24px_-18px_rgba(160,82,45,0.3)]">
        <p className="overline text-[#A0522D] mb-3">Scrivi una nuova campagna</p>
        <p className="text-xs text-[#6E5B4A] mb-4">
          Usa un modello pre-compilato qui sotto oppure scrivi tutto da zero. Verrà inviata a <strong>{subscribers.length}</strong> {subscribers.length === 1 ? "iscritta" : "iscritte"}.
        </p>

        {/* Templates */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => applyTemplate(buildNewWorkshopTemplate)}
            data-testid="template-new-workshop"
            className="btn-ghost !py-1.5 !px-3 !text-xs"
          >
            📅 Nuovo workshop
          </button>
          <button
            type="button"
            onClick={() => applyTemplate(buildNewDateTemplate)}
            data-testid="template-new-date"
            className="btn-ghost !py-1.5 !px-3 !text-xs"
          >
            🗓️ Nuova data
          </button>
          <button
            type="button"
            onClick={() => applyTemplate(buildNewLessonTemplate)}
            data-testid="template-new-lesson"
            className="btn-ghost !py-1.5 !px-3 !text-xs"
          >
            🩰 Nuova lezione
          </button>
        </div>

        <EditField label="Oggetto email">
          <input
            data-testid="newsletter-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="input-warm"
            maxLength={200}
            placeholder="Es. Nuovo workshop in arrivo!"
          />
        </EditField>
        <EditField label="Testo del messaggio (le righe vuote separano i paragrafi)">
          <textarea
            data-testid="newsletter-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            maxLength={10000}
            className="input-warm !leading-relaxed font-mono !text-sm"
            placeholder={"Ciao!\n\nHo una novità da raccontarti...\n\nA presto,\nAlessandro"}
          />
        </EditField>

        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={sendCampaign}
            disabled={sending || !subject.trim() || !body.trim() || subscribers.length === 0}
            data-testid="send-newsletter"
            className="btn-primary disabled:opacity-40"
          >
            {sending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Send size={14} />
            )}
            Invia a {subscribers.length} {subscribers.length === 1 ? "iscritta" : "iscritte"}
          </button>
        </div>
      </div>

      {/* SUBSCRIBERS LIST */}
      <div className="flex justify-between items-center mb-4">
        <p className="overline text-[#A0522D]">
          {subscribers.length} {subscribers.length === 1 ? "iscritta" : "iscritte"} totali
        </p>
        <div className="flex gap-2">
          <button
            onClick={exportCSV}
            disabled={!subscribers.length}
            data-testid="newsletter-export-csv"
            className="btn-ghost !py-2 !px-4"
          >
            <Download size={14} />
            <span className="hidden sm:inline">Esporta CSV</span>
          </button>
          <button
            onClick={fetchSubscribers}
            disabled={loading}
            className="btn-ghost !py-2 !px-4"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            <span className="hidden sm:inline">Aggiorna</span>
          </button>
        </div>
      </div>

      {loading && subscribers.length === 0 ? (
        <div className="text-center py-16 text-[#6E5B4A]">
          <Loader2 size={28} className="animate-spin mx-auto" />
        </div>
      ) : subscribers.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#D9C5A4] rounded-sm bg-[#FBF6EA]">
          <p className="overline text-[#6E5B4A]">Nessun iscritto ancora</p>
          <p className="text-sm text-[#A8987F] mt-2">
            Le iscrizioni alla newsletter compaiono qui appena qualcuno completa il popup sulla home.
          </p>
        </div>
      ) : (
        <div className="bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm overflow-hidden">
          {subscribers.map((s, i) => (
            <div
              key={s.id}
              data-testid={`newsletter-row-${i}`}
              className={`flex items-center justify-between gap-3 px-4 py-3 ${
                i > 0 ? "border-t border-[#D9C5A4]" : ""
              }`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-[#2A1810] text-sm truncate">
                  {s.name ? <strong className="mr-2">{s.name}</strong> : null}
                  {s.email}
                </p>
                <p className="text-[10px] text-[#A8987F] mt-0.5 tracking-wider uppercase">
                  Iscritta il {new Date(s.created_at).toLocaleDateString("it-IT")}
                </p>
              </div>
              <button
                onClick={() => removeSubscriber(s.id)}
                data-testid={`newsletter-delete-${s.id}`}
                aria-label="rimuovi"
                className="text-[#6E5B4A] hover:text-[#A0522D] hover:bg-[#A0522D]/10 p-2 rounded-sm transition"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

